import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Users, CalendarClock, ArrowRight, TrendingUp, Sparkles, ShieldCheck } from 'lucide-react';
import { listListings, listClients, listAppointments, getClientsStats } from './crmApi';
import { api } from '../../context/AuthContext';

function StatCard({ label, value, icon: Icon, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center justify-between bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div>
        <div className="text-sm text-brand-green/60">{label}</div>
        <div className="text-3xl font-serif text-brand-green mt-1">{value}</div>
      </div>
      <Icon className="w-10 h-10 text-brand-gold group-hover:scale-110 transition-transform" />
    </Link>
  );
}

const PIPELINE_TILE_META = {
  buyer:    { label: 'Buyers',    color: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-500' },
  seller:   { label: 'Sellers',   color: '#B8860B', bg: 'bg-amber-50',   text: 'text-amber-800',   border: 'border-amber-500' },
  investor: { label: 'Investors', color: '#7C3AED', bg: 'bg-violet-50',  text: 'text-violet-800',  border: 'border-violet-500' },
};

function PipelineTile({ type, stats }) {
  const meta = PIPELINE_TILE_META[type];
  const total = stats?.total ?? 0;
  const week = stats?.this_week ?? 0;
  return (
    <Link to={`/crm/clients?type=${type}`}
          data-testid={`pipeline-tile-${type}`}
          className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow border-l-4 ${meta.border} ${meta.bg}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-xs uppercase tracking-wide font-semibold ${meta.text}`}>{meta.label}</div>
          <div className="text-3xl font-serif text-brand-green mt-1">{total}</div>
          <div className={`text-xs mt-1 ${meta.text}`}>
            <TrendingUp className="w-3 h-3 inline" /> {week} new this week
          </div>
        </div>
      </div>
    </Link>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}

export default function CrmDashboard() {
  const [listings, setListings] = useState([]);
  const [clients, setClients] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [aiAudit, setAiAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ls, cs, up, pl, audit] = await Promise.all([
          listListings(),
          listClients(),
          listAppointments('upcoming'),
          getClientsStats().catch(() => null),
          api.get('/ai-audit').then((r) => r.data).catch(() => null),
        ]);
        if (cancelled) return;
        setListings(ls);
        setClients(cs);
        setUpcoming(up);
        setPipeline(pl);
        setAiAudit(audit);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recentClients = useMemo(() => {
    return [...clients]
      .sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''))
      .slice(0, 5);
  }, [clients]);

  if (loading) {
    return <div data-testid="crm-dashboard-loading" className="text-brand-green/60">Loading your workspace…</div>;
  }

  return (
    <div data-testid="crm-dashboard" className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Listings" value={listings.length} icon={HomeIcon} to="/crm/listings" testid="crm-stat-listings" />
        <StatCard label="Upcoming appointments" value={upcoming.length} icon={CalendarClock} to="/crm/appointments" testid="crm-stat-upcoming" />
        <StatCard label="Clients" value={clients.length} icon={Users} to="/crm/clients" testid="crm-stat-clients" />
      </div>

      {pipeline && (
        <div>
          <div className="text-sm font-semibold text-brand-green/70 mb-2 inline-flex items-center gap-2">
            Pipeline mix
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="crm-pipeline-tiles">
            <PipelineTile type="buyer" stats={pipeline.buyer} />
            <PipelineTile type="seller" stats={pipeline.seller} />
            <PipelineTile type="investor" stats={pipeline.investor} />
          </div>
        </div>
      )}

      {aiAudit && (
        <div
          data-testid="crm-ai-audit-card"
          className="relative bg-brand-green text-brand-cream rounded-xl p-5 sm:p-6 shadow-sm overflow-hidden"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 rounded-lg bg-brand-cream/10 p-2.5">
              <Sparkles className="w-5 h-5 text-brand-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-cream/70">
                <ShieldCheck className="w-3.5 h-3.5" />
                Third-party AI processing (this month)
              </div>
              <div className="mt-1.5 text-2xl sm:text-3xl font-serif leading-tight">
                <span data-testid="crm-ai-audit-month-count" className="text-brand-cream">
                  {aiAudit.this_month?.processed_calls ?? 0}
                </span>{' '}
                <span className="text-brand-cream/70 text-lg sm:text-xl">
                  call{(aiAudit.this_month?.processed_calls ?? 0) === 1 ? '' : 's'} processed by{' '}
                  <span className="text-brand-gold">Google Gemini</span>
                </span>
              </div>
              <p className="mt-2 text-sm text-brand-cream/75 leading-snug">
                Every AI call is transcribed and answered by Google Gemini (Google LLC).
                No data is used to train Google&rsquo;s models.
                <span className="text-brand-cream/60">
                  {' '}Lifetime:{' '}
                  <span data-testid="crm-ai-audit-lifetime-count" className="text-brand-cream/90 font-semibold">
                    {aiAudit.all_time?.processed_calls ?? 0}
                  </span>.
                </span>
              </p>
              <Link
                to="/privacy#p9"
                data-testid="crm-ai-audit-privacy-link"
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-gold hover:text-brand-cream underline underline-offset-4 decoration-brand-gold/60"
              >
                See exactly what data is sent <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="crm-upcoming-list">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-brand-green">Next up</h2>
            <Link to="/crm/appointments" className="text-sm text-brand-green/70 inline-flex items-center gap-1 hover:text-brand-green">
              All appointments <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-brand-green/60">No upcoming appointments — you're wide open.</p>
          ) : (
            <ul className="divide-y divide-brand-cream">
              {upcoming.slice(0, 5).map((a) => (
                <li key={a.id} className="py-3">
                  <div className="text-sm font-semibold text-brand-green">{a.title || 'Showing'}</div>
                  <div className="text-xs text-brand-green/70">
                    {fmtDate(a.start)} · {a.duration_min} min
                    {a.client_name ? ` · ${a.client_name}` : ''}
                  </div>
                  {a.address && <div className="text-xs text-brand-green/60">{a.address}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="crm-recent-clients">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-xl text-brand-green">Recent clients</h2>
            <Link to="/crm/clients" className="text-sm text-brand-green/70 inline-flex items-center gap-1 hover:text-brand-green">
              All clients <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {recentClients.length === 0 ? (
            <p className="text-sm text-brand-green/60">No clients yet — add one from the Clients tab.</p>
          ) : (
            <ul className="divide-y divide-brand-cream">
              {recentClients.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-brand-green">{c.name}</div>
                    <div className="text-xs text-brand-green/70">
                      {c.type} · {c.phone_number || c.email || '—'}
                    </div>
                  </div>
                  {c.tag && (
                    <span className="text-xs px-2 py-1 rounded-full bg-brand-cream text-brand-green">{c.tag}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
